import React, { useState } from 'react';
import PropTypes from "prop-types";
import { css } from '@emotion/react';
import { useEuiTheme } from '../../../../../src/services';
import { EuiBasicTable, EuiButtonGroup, EuiCode, EuiCodeBlock, EuiDescribedFormGroup, EuiPanel, EuiSpacer } from '../../../../../src/components';
import { euiFontSize, useEuiFontSize, EuiThemeFontWeights, EuiThemeFontScales, EuiThemeFontSizeMeasurements } from '../../../../../src/global_styling';
import { EuiThemeFontBase, EuiThemeFontWeight } from '../_props';
import { getPropsFromComponent } from '../../../services/props/get_props';
import { getDescription } from '../../../services/props/get_description';
import { ThemeExample } from '../_components/_theme_example';
import { ThemeValuesTable } from '../_components/_theme_values_table';
export var FontJS = function FontJS() {
  var _useEuiTheme = useEuiTheme(),
    euiTheme = _useEuiTheme.euiTheme;
  var baseProps = getPropsFromComponent(EuiThemeFontBase);
  return ___EmotionJSX(React.Fragment, null, ___EmotionJSX(ThemeExample, {
    title: ___EmotionJSX("code", null, "euiTheme.font.family"),
    description: ___EmotionJSX(React.Fragment, null, getDescription(baseProps.family), ___EmotionJSX("br", null), ___EmotionJSX("p", null, "All of EUI defaults to the base ", ___EmotionJSX(EuiCode, null, "font.family"), ". However, you can use this token to change specific instances.")),
    example: ___EmotionJSX("p", {
      css: /*#__PURE__*/css("font-family:", euiTheme.font.family, ";;label:FontJS;")
    }, euiTheme.font.family),
    snippet: 'font-family: ${euiTheme.font.family};',
    snippetLanguage: "emotion"
  }), ___EmotionJSX(ThemeExample, {
    title: ___EmotionJSX("code", null, "euiTheme.font.familyCode"),
    description: getDescription(baseProps.familyCode),
    example: ___EmotionJSX("p", {
      css: /*#__PURE__*/css("font-family:", euiTheme.font.familyCode, ";;label:FontJS;")
    }, euiTheme.font.familyCode),
    snippet: 'font-family: ${euiTheme.font.familyCode};',
    snippetLanguage: "emotion"
  }), ___EmotionJSX(ThemeExample, {
    title: ___EmotionJSX("code", null, "euiTheme.font.familySerif"),
    description: getDescription(baseProps.familySerif),
    example: ___EmotionJSX("p", {
      css: /*#__PURE__*/css("font-family:", euiTheme.font.familySerif, ";;label:FontJS;")
    }, euiTheme.font.familySerif),
    snippet: 'font-family: ${euiTheme.font.familySerif};',
    snippetLanguage: "emotion"
  }), ___EmotionJSX(ThemeExample, {
    title: ___EmotionJSX("code", null, "euiTheme.font.featureSettings"),
    description: getDescription(baseProps.featureSettings),
    example: ___EmotionJSX("p", {
      css: /*#__PURE__*/css("font-feature-settings:", euiTheme.font.featureSettings, ";;label:FontJS;")
    }, euiTheme.font.featureSettings),
    snippet: 'font-feature-settings: ${euiTheme.font.featureSettings};',
    snippetLanguage: "emotion"
  }));
};
export var FontWeightJS = ({
  description
}) => {
  const {
    euiTheme
  } = useEuiTheme();
  return <>
      <ThemeExample title={<code>euiTheme.font.weight[weight]</code>} description={description} example={<div css={/*#__PURE__*/css("font-weight:", euiTheme.font.weight.bold, ";;label:FontWeightJS;")}>
            {'I am proper bold'}
          </div>} snippet={'font-weight: ${euiTheme.font.weight.bold};'} snippetLanguage="emotion" />
    </>;
};
FontWeightJS.propTypes = {
  description: PropTypes.node
};
export const FontWeightValuesJS = () => {
  const euiThemeContext = useEuiTheme();
  const {
    euiTheme
  } = euiThemeContext;
  const weightProps = getPropsFromComponent(EuiThemeFontWeight);
  const weightKeys = EuiThemeFontWeights;
  return <>
      <ThemeValuesTable items={weightKeys.map(weight => {
      return {
        id: weight,
        token: `font.weight.${weight}`,
        type: weightProps[weight],
        value: euiTheme.font.weight[weight]
      };
    })} render={item => <div css={/*#__PURE__*/css("font-weight:", item.value, ";;label:FontWeightValuesJS;")}>
            Aa
          </div>} />
    </>;
};
export const FontScaleJS = () => {
  return <>
      <ThemeExample title={<code>useEuiFontSize()</code>} type="hook" description={<p>
            Font sizing is provided through this React hook (or function
            version) and not the global theme. It returns both the{' '}
            <EuiCode>font-size</EuiCode> and <EuiCode>line-height</EuiCode> for
            the provided <EuiCode>scale</EuiCode>.
          </p>} example={<p css={/*#__PURE__*/css(useEuiFontSize('l'), ";;label:FontScaleJS;")}>
            The quick brown fox jumped over the blue moon to catch a snail
          </p>} snippet="${useEuiFontSize('l')}" snippetLanguage="emotion" />
      <ThemeExample title={<code>useEuiFontSize().fontSize</code>} type={null} description={<p>
            To use precisely only the <EuiCode>font-size</EuiCode> value, you
            will still use the same hook (or function) to grab the individual
            property via the returned object.
          </p>} example={<p css={/*#__PURE__*/css("font-size:", useEuiFontSize('xs').fontSize, ";;label:FontScaleJS;")}>
            The quick brown fox jumped over the blue moon to catch a snail
          </p>} snippet="font-size: ${useEuiFontSize('xs').fontSize};" snippetLanguage="emotion" />
    </>;
};
export const FontScaleValuesJS = () => {
  const euiThemeContext = useEuiTheme();
  const scaleKeys = EuiThemeFontScales;
  const measurementButtons = EuiThemeFontSizeMeasurements.map(m => {
    return {
      id: m,
      label: m
    };
  });
  const [measurementSelected, setMeasurementSelected] = useState(measurementButtons[0].id);
  return <>
      <EuiPanel color="accent">
        <EuiDescribedFormGroup fullWidth title={<h3>Value measurements</h3>} description={<p>
              The font sizing function also supports the three main measurements
              for font-size, <EuiCode>rem | px | em</EuiCode>, with{' '}
              <EuiCode>rem</EuiCode> being default for all EUI components.
            </p>}>
          <EuiSpacer />
          <EuiButtonGroup buttonSize="m" legend="Value measurement to show in table" options={measurementButtons} idSelected={measurementSelected} onChange={id => setMeasurementSelected(id)} color="accent" isFullWidth />
        </EuiDescribedFormGroup>
      </EuiPanel>
      <EuiSpacer />
      <EuiBasicTable tableLayout="auto" items={scaleKeys.map((scale, index) => {
      return {
        id: scale,
        value: `useEuiFontSize('${scale}'${measurementSelected !== 'rem' ? `,\n  { measurement: '${measurementSelected}' }\n` : ''})`,
        size: `${euiFontSize(euiThemeContext, scale, {
          measurement: measurementSelected
        }).fontSize}`,
        lineHeight: `${euiFontSize(euiThemeContext, scale, {
          measurement: measurementSelected
        }).lineHeight}`,
        index
      };
    })} columns={[{
      field: 'sample',
      name: 'Sample',
      valign: 'baseline',
      width: '50%',
      render: (sample, item) => <div css={/*#__PURE__*/css(euiFontSize(euiThemeContext, item.id, {
        measurement: measurementSelected
      }), ";;label:render;")}>
                The quick brown fox jumped over the blue moon to catch a snail
              </div>,
      mobileOptions: {
        width: '100%'
      }
    }, {
      field: 'value',
      name: 'Function',
      width: 'auto',
      valign: 'baseline',
      render: value => <EuiCodeBlock language="js" paddingSize="s">
                {value}
              </EuiCodeBlock>
    }, {
      field: 'size',
      name: 'Font size',
      width: '100px',
      valign: 'baseline',
      align: 'right',
      render: size => <small>
                <code>{size}</code>
              </small>
    }, {
      field: 'lineHeight',
      name: 'Line height',
      width: '100px',
      valign: 'baseline',
      align: 'right',
      render: lineHeight => <small>
                <code>{lineHeight}</code>
              </small>
    }]} />
    </>;
};
try {
  FontWeightJS.__docgenInfo = {
    tags: {},
    filePath: '/Users/briannahall/Documents/EUI/eui/src-docs/src/views/theme/typography/_typography_js.tsx',
    description: '',
    displayName: 'FontWeightJS',
    methods: [],
    props: {
      description: {
        defaultValue: null,
        description: '',
        name: 'description',
        parent: undefined,
        declarations: [{
          fileName: 'eui/src-docs/src/views/theme/_props.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'React.ReactNode'
        }
      }
    },
    extendedInterfaces: []
  };
} catch (e) {}